import { useQuery } from '@apollo/client';
import { GET_PRO } from '@gql/pros';
import { FormControl, FormHelperText, FormLabel, MenuItem, Select } from '@mui/material';
import { CreditsFeature } from '@utils/permissions';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

type FeatureCreditsSelectFieldProps = {
    proId?: string;
    feature: CreditsFeature;
    name?: string;
    label?: string;
    requiredMessage?: string;
};

const FeatureCreditsSelectField = ({
    proId,
    feature,
    name = 'featureId',
    label = 'Crédits disponibles',
    requiredMessage = 'Veuillez sélectionner un crédit.',
}: FeatureCreditsSelectFieldProps) => {
    const { control , setValue } = useFormContext();

    const { data } = useQuery(GET_PRO, {
        variables: { proId },
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-first',
        skip: !proId,
    });

    const pro = data?.getPro;

    const credits = useMemo(() => {
        return pro?.credits?.filter((item: any) => item.key === feature) || [];
    }, [pro, feature]);

    const creditOptions = useMemo(
        () =>
            credits?.map(credit => {
                const { maximum, used } = credit.configuration;
                const maxValue = maximum === -1 ? '∞' : maximum;

                return {
                    value: credit._id,
                    label: `${credit.planName || credit.name} (${used}/${maxValue})`,
                };
            }) || [],
        [credits],
    );

    useEffect(() => {
        if (creditOptions.length === 1) {
            setValue(name, creditOptions[0].value);
        }
    }, [creditOptions, setValue, name]);

    return (
        <Controller
            name={name}
            control={control}
            rules={{ required: requiredMessage }}
            render={({ field, fieldState }) => (
                <FormControl fullWidth error={!!fieldState.error}>
                    <FormLabel>{label}</FormLabel>
                    <Select
                        {...field}
                        size="medium"
                        displayEmpty
                        value={field.value || ''}
                        disabled={!proId || creditOptions.length === 0}
                    >
                        {creditOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        {fieldState.error?.message ||
                            (!proId
                                ? 'Sélectionnez d\'abord un compte entreprise.'
                                : creditOptions.length === 0
                                    ? 'Aucun crédit disponible pour cette fonctionnalité.'
                                    : null)}
                    </FormHelperText>
                </FormControl>
            )}
        />
    );
};

export default FeatureCreditsSelectField;
